// Client for /api/permissions/my — the granular (module/tab/field ×
// view/edit/delete) permission system. Same cached-fetch shape as
// src/formCatalog.js: one in-flight request shared by every caller,
// invalidated after an admin saves a role template or a user's own grants
// (via the 'permissions:changed' socket push below, or on tab-focus as a
// fallback for anyone whose socket connection dropped).

import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import api, { BASE } from "./api";

let cache = null;
let cacheToken = null;

// One fetch shared by every caller — but tied to the login it was fetched for. Signing out and in as someone
// else in the same tab used to keep showing the PREVIOUS person's permissions until a full reload.
const currentToken = () => { try { return localStorage.getItem("hrms_token"); } catch (_) { return null; } };

export function loadPermissions() {
  const tk = currentToken();
  if (!cache || cacheToken !== tk) {
    cacheToken = tk;
    cache = api.get("/api/permissions/my")
      .then((d) => d)
      .catch((e) => { cache = null; throw e; });
  }
  return cache;
}

export function invalidatePermissions() { cache = null; }

// can(module, tab, field, action='view') — admin is always true. A node's
// own grant is authoritative at its own level: a field's view grant is
// checked directly, it does not fall back to its tab's or module's grant
// (callers that only need module/tab-level gating just omit the deeper
// args, e.g. can('employees', tabId, null, 'view') for a tab).
function makeCan(grants, role, roleHint = null) {
  return (module, tab, field, action = "view") => {
    // An Admin has every permission, always — also before the list has loaded and even if it failed to load.
    if (role === "admin" || roleHint === "admin" || grants === "*") return true;
    const key = [module, tab, field].filter(Boolean).join(".");
    return (grants?.[key] || []).includes(action);
  };
}

// `enabled` lets a caller defer fetching until it's actually safe to (e.g.
// App.js passes token != null — this hook has no way to know on its own
// whether the user is logged in yet, and an unauthenticated request would
// just 401 and log a spurious error).
export function usePermissions(enabled = true, roleHint = null) {
  const [state, setState] = useState({ role: null, grants: null, loading: enabled, error: null });

  useEffect(() => {
    if (!enabled) { setState({ role: null, grants: null, loading: false, error: null }); return; }
    let live = true;
    let myEmployeeId = null;
    let myRole = roleHint; // best guess until the first fetch resolves
    setState((s) => ({ ...s, loading: true }));
    const refetch = () => {
      invalidatePermissions();
      return loadPermissions().then((d) => {
        myEmployeeId = d.employee_id || myEmployeeId;
        myRole = d.role || myRole;
        if (live) setState({ role: d.role, grants: d.grants, loading: false, error: null });
        return d;
      });
    };
    loadPermissions()
      .then((d) => { myEmployeeId = d.employee_id || null; myRole = d.role || myRole; if (live) setState({ role: d.role, grants: d.grants, loading: false, error: null }); })
      .catch((e) => { if (live) setState({ role: null, grants: null, loading: false, error: e.message || "Couldn't load permissions" }); });

    // Instant path: an admin saving a role template or a user's individual
    // grants pushes 'permissions:changed' ({role} or {employee_id}) over
    // the same socket.io connection chat already uses. Only act on it if
    // it actually concerns this logged-in person — a role-template edit
    // for a role we're not on, or someone else's individual override,
    // isn't worth a refetch.
    const token = currentToken();
    const socket = token ? io(BASE, { auth: { token }, transports: ["websocket", "polling"] }) : null;
    if (socket) {
      socket.on("permissions:changed", (payload) => {
        if (!payload) return;
        const mine = (payload.role && payload.role === myRole) || (payload.employee_id && payload.employee_id === myEmployeeId);
        if (mine) refetch().catch(() => {});
      });
    }

    // Fallback for anyone whose socket connection dropped/never connected
    // (flaky network, blocked websockets): still pick up changes on tab
    // focus, same as before, just with a longer leash now that the socket
    // is the primary path.
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 60000) return;
      last = Date.now();
      refetch().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisible);
      if (socket) socket.disconnect();
    };
  }, [enabled, currentToken()]);

  return { ...state, can: makeCan(state.grants, state.role, roleHint) };
}
